import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import alertMetadata from 'scoreboard/alerts/alert-metadata'
import metadata from 'scoreboard/components/metadata'
import unwrapModel from 'scoreboard/components/unwrap-model'
import exportMetadata from 'scoreboard/components/export/export-metadata'
import enums from 'scoreboard/components/enums'
import fileInfo from 'scoreboard/components/files/file-info'

const Alert = Backbone.Model.extend(/** @lends Alert.prototype */{

	initialize: function() {
		if (this.isExportReady() || this.isExportFailed()) {
			// Push notifications use `properties`, persisted alerts have label/url as main properties
			const attr = unwrapModel(this)
			const {label, url} = _.get(attr, 'properties', attr)
			const extension = fileInfo.extension(label)
			this._url = url
			this._exportMetadata = exportMetadata[extension]
			this._exportDomainLabel = fileInfo.fileName(label)
			this._exportTypeLabel = enums.exportType[this._exportMetadata.type]
		}
	},

	isBroadcast: function() {
		return this.get('type') === alertMetadata.types.BROADCAST
	},

	/**
	 * An alert sent to a user when their export download is ready
	 */
	isExportReady: function() {
		return this.get('type') === alertMetadata.types.EXPORT_READY
	},

	isExportFailed: function() {
		return this.get('type') === alertMetadata.types.EXPORT_FAILED
	},

	isMetricValueRequiresApproval: function() {
		return this.get('type') === alertMetadata.types.METRIC_VALUE_REQUIRES_APPROVAL
	},

	isMetricValuesReviewed: function() {
		return this.get('type') === alertMetadata.types.METRIC_VALUES_REVIEWED
	},

	isAccountChanged: function() {
		return this.get('type') === alertMetadata.types.ACCOUNT_CHANGED
	},

	isAccountDisabled: function() {
		return this.get('type') === alertMetadata.types.ACCOUNT_DISABLED
	},

	/** an alert generated as a result of a form action */
	isFormAlert: function() {
		return this.get('type') === alertMetadata.types.FORM_ALERT
	},

	getExportFailedMessage() {
		const domainLink = `<a ` +
			`href="${this._url}" class="sp-navigate text-link">` +
			`${_.escape(this._exportDomainLabel)}` +
		`</a>`

		return i18n.get('common.export.alert.failed', this._exportTypeLabel, domainLink)
	},

	/**
	 * An alert generated as a result of a scheduled export being triggered
	 */
	isScheduledExport: function() {
		return this.get('type') === alertMetadata.types.SCHEDULED_EXPORT
	},

	/**
	 * An alert generated because a modifier manually published a change
	 */
	isPublished: function() {
		return this.get('type') === alertMetadata.types.CHANGE_PUBLISHED
	},

	/**
	 * @return true if clicking the alert should trigger the file to be downloaded
	 */
	isFileBased() {
		// do NOT include change published (subscription) alerts here.
		// They should always navigate to the domain object in the app instead (24018)
		return this.isExportReady() || this.isScheduledExport()
	},

	/**
	 * @return true if the alert has a file available for download
	 * (may have been deleted after the expiration date, 2 months)
	 */
	hasFile() {
		return this.get('fileId')
	},

	/**
	 * @return true if this alert is a type that references a file, but that file
	 * has been deleted from the database
	 */
	hasDeletedFile() {
		return this.isFileBased() && !this.hasFile()
	},

	getMessage: function() {
		const attributes = this.attributes
		if (this.isExportReady()) {
			// Push alert has properties, /api/alerts is a flattened object (see Alert.java)
			const {fileId} = attributes.properties || attributes
			return `<span class="download" data-fileid="${fileId}">` +
				`${_.escape(i18n.get('common.export.longrunning.ready'))}` +
			`</span>`
		}
		return attributes.message
	},

	/**
	 * Gets the icon to display alongside the alert
	 * @param [domainItem]
	 *
	 * @return {icon, iconClass}
	 * iconClass example : `icon has-color red`
	 */
	getIcon: function(domainItem) {
		const alert = this.attributes
		const context = {iconClass: `icon${alert.color ? ' has-color ' + alert.color : ''}`}

		if (this.isMetricValueRequiresApproval()) {
			return { icon: 'metric', iconClass: '' }
		}
		if (this.isAccountChanged() || this.isAccountDisabled()) {
			return { icon: 'warn', iconClass: '' }
		}
		if (this._exportMetadata) {
			context.icon = this._exportMetadata.icon
		} else if (this.isBroadcast() || this.isPublished()) {
			context.icon = 'broadcast'
		} else {
			const domainType = _.get(domainItem, 'type')
			const deletedDomainObject = alert.domainId && _.isNil(domainItem)
			context.icon = getIconForAlertOrSubscription(alert, domainType, deletedDomainObject)
		}
		return context
	}
})

/**
 * Gets the icon to display for an alert or subscription based on
 * it's type and domainType.
 *
 * @param alertOrSubscription
 * @param {String} [domainType=alertOrSubscription.domainType]
 * @param {Boolean} [deletedDomainObject=false]
 */
function getIconForAlertOrSubscription(alertOrSubscription,
	domainType, deletedDomainObject) {

	const dt = domainType || alertOrSubscription.domainType
	let icon
	if (_.startsWith(alertOrSubscription.type, 'comment-')) {
		icon = 'note'
	} else if (deletedDomainObject) {
		icon = alertOrSubscription.domainType
		if (icon === 'scorecard-item') {
			icon = 'root'
		}
	} else if (dt) {
		const md = metadata[dt]
		icon = md ? md.sectionIcon : dt
	} else {
		icon = alertOrSubscription.type
	}
	return icon
}

export default Alert
export { getIconForAlertOrSubscription }

Alert.__filename__ = 'alerts/alert.js'
